<template>
  <header class="site-header">
    <div class="site-header__inner">
      <button type="button" class="brand-lockup" @click="handleNavigateHome">
        <span class="brand-mark">VC</span>
        <span class="brand-copy">
           <strong>视核云听</strong>
           <small>videocore</small>
        </span>
      </button>

      <nav v-if="navItems.length" class="site-nav" aria-label="主导航">
        <button
          v-for="item in navItems"
          :key="item.path"
          type="button"
          class="site-nav__link"
          :class="{ 'site-nav__link--active': isActive(item.path) }"
          @click="handleNavigate(item.path)"
        >
          {{ item.label }}
        </button>
      </nav>

      <div class="site-actions">
        <template v-if="userStore.isAuthenticated">
          <el-button v-if="route.path !== '/submit'" class="create-button" type="primary" @click="router.push('/submit')">创建项目</el-button>

          <el-dropdown trigger="click" @command="handleCommand">
            <button type="button" class="account-trigger">
              <span class="account-avatar">{{ userInitial }}</span>
              <span class="account-copy">
                <strong>{{ userStore.userInfo?.username || '当前用户' }}</strong>
              </span>
              <el-icon class="account-arrow"><ArrowDown /></el-icon>
            </button>

            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item disabled>{{ roleLabel }}</el-dropdown-item>
                <el-dropdown-item command="dashboard">项目工作台</el-dropdown-item>
                <el-dropdown-item command="task-square">任务广场</el-dropdown-item>
                <el-dropdown-item v-if="userStore.canAccessAdmin" command="admin">管理后台</el-dropdown-item>
                <el-dropdown-item divided command="logout">退出登录</el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </template>

        <template v-else>
          <el-button @click="router.push('/login')">登录</el-button>
          <el-button type="primary" @click="notifyRegistrationUnavailable">注册</el-button>
        </template>
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import { computed, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { ArrowDown } from '@element-plus/icons-vue'

import { notifyRegistrationUnavailable } from '@/utils/authNavigation'
import { useUserStore } from '@/stores/user'

const route = useRoute()
const router = useRouter()
const userStore = useUserStore()

const navItems = computed(() => {
  if (!userStore.isAuthenticated) {
    return []
  }

  return [
    { label: '首页', path: '/' },
    { label: '任务广场', path: '/task-square' },
    { label: '项目工作台', path: '/dashboard' },
    { label: '素材参考中心', path: '/knowledge' },
    { label: '自成长智能体', path: '/learning' },
  ]
})

const userInitial = computed(() => (userStore.userInfo?.username || 'U').charAt(0).toUpperCase())

const roleLabel = computed(() => {
  if (userStore.isSuperAdmin) return '超级管理员'
  if (userStore.isAdmin) return '管理员'
  return '普通用户'
})

const isActive = (path: string) => {
  if (path === '/') {
    return route.path === '/'
  }
  return route.path.startsWith(path)
}

const scrollToTop = () => {
  if (typeof window !== 'undefined') {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
}

const handleNavigate = async (path: string) => {
  if (route.path === path) {
    scrollToTop()
    return
  }

  await router.push(path)
  await nextTick()
  scrollToTop()
}

const handleNavigateHome = async () => {
  await handleNavigate('/')
}

const handleCommand = (command: string | number | object) => {
  if (command === 'dashboard') {
    router.push('/dashboard')
    return
  }

  if (command === 'task-square') {
    router.push('/task-square')
    return
  }

  if (command === 'admin') {
    router.push('/admin')
    return
  }

  if (command === 'logout') {
    userStore.logout()
    ElMessage.success('已退出登录')
    router.push('/login')
  }
}
</script>

<style scoped>
.site-header {
  position: sticky;
  top: 0;
  z-index: 40;
  border-bottom: 1px solid rgba(148, 163, 184, 0.16);
  background: rgba(248, 250, 255, 0.88);
  backdrop-filter: blur(18px);
}

.site-header__inner {
  max-width: 1280px;
  margin: 0 auto;
  padding: 18px 32px;
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 24px;
}

.brand-lockup {
  border: 0;
  padding: 0;
  display: inline-flex;
  align-items: center;
  gap: 14px;
  color: #0f172a;
  background: transparent;
  cursor: pointer;
}

.brand-mark {
  width: 42px;
  height: 42px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #356dff, #5b38f7);
  color: #fff;
  font-size: 15px;
  font-weight: 800;
  letter-spacing: 0.04em;
  box-shadow: 0 12px 28px rgba(79, 92, 241, 0.24);
}

.brand-copy {
  display: flex;
  flex-direction: column;
}

.brand-copy strong {
  font-size: 17px;
  font-weight: 700;
}

.brand-copy small {
  color: #64748b;
  font-size: 12px;
  letter-spacing: 0.04em;
}

.site-nav {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  flex-wrap: wrap;
}

.site-nav__link {
  border: 0;
  display: inline-flex;
  align-items: center;
  min-height: 38px;
  padding: 0 16px;
  border-radius: 999px;
  background: transparent;
  color: #475569;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;
  cursor: pointer;
}

.site-nav__link:hover,
.site-nav__link--active {
  background: rgba(53, 109, 255, 0.1);
  color: #1d4ed8;
}

.site-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
}

.create-button {
  border-radius: 999px;
  padding-inline: 18px;
  box-shadow: 0 12px 24px rgba(53, 109, 255, 0.2);
}

.account-trigger {
  min-width: 0;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 4px 8px 4px 4px;
  border: 1px solid rgba(88, 114, 173, 0.12);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.52);
  box-shadow: 0 6px 18px rgba(54, 83, 164, 0.06);
  cursor: pointer;
  transition: background 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

.account-trigger:hover {
  background: rgba(255, 255, 255, 0.74);
  border-color: rgba(53, 109, 255, 0.18);
  box-shadow: 0 10px 22px rgba(54, 83, 164, 0.1);
}

.account-avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(53, 109, 255, 0.12);
  color: #2740b8;
  font-size: 13px;
  font-weight: 800;
  border: 1px solid rgba(53, 109, 255, 0.1);
  flex-shrink: 0;
}

.account-copy {
  display: flex;
  text-align: left;
  align-items: center;
  line-height: 1;
  min-width: 0;
}

.account-copy strong {
  color: #0f172a;
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;
}

.account-arrow {
  color: #94a3b8;
  font-size: 14px;
  flex-shrink: 0;
}

@media (max-width: 960px) {
  .site-header__inner {
    grid-template-columns: 1fr;
    justify-items: stretch;
    padding: 16px 18px;
  }

  .site-nav {
    justify-content: flex-start;
  }

  .site-actions {
    justify-content: space-between;
    flex-wrap: wrap;
  }

  .account-trigger {
    margin-left: auto;
  }
}

@media (max-width: 640px) {
  .brand-copy small,
  .account-copy {
    display: none;
  }

  .account-trigger {
    padding-right: 6px;
    gap: 8px;
  }

  .site-actions {
    gap: 8px;
  }
}
</style>